import capitalize from 'lodash/capitalize'
import { Days } from 'components/Common/types'
import { DiscountTarget } from 'components/Discounts/DiscountForm/types'
import { DAYS } from 'components/Locations/AvailabilityRules/constants'
import {
  DayWindow,
  LoyaltyMultiplierDay,
  LoyaltyMultiplierListItem,
  LoyaltyMultiplierStatus
} from './types'

export const isAllDayWindow = (window?: DayWindow): boolean =>
  window?.start_time === '00:00' && window?.end_time === '23:59'

/** "TUESDAY" -> "Tue", for compact day lists */
export const abbreviateDay = (day: Days) => capitalize(day).slice(0, 3)

export const pluralise = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`

/** Outlined badge text and border colour, per the status design. The theme has no matching tokens for these. */
export const STATUS_DISPLAY: Record<
  LoyaltyMultiplierStatus,
  { label: string; color: string }
> = {
  LIVE: { label: 'Live', color: '#2f54eb' },
  SCHEDULED: { label: 'Scheduled', color: '#d46b08' },
  PAUSED: { label: 'Paused', color: '#8c8c8c' },
  EXPIRED: { label: 'Expired', color: '#cf1322' },
  ARCHIVED: { label: 'Archived', color: '#8c8c8c' }
}

const STATUS_RANK: Record<LoyaltyMultiplierStatus, number> = {
  LIVE: 0,
  SCHEDULED: 1,
  PAUSED: 2,
  EXPIRED: 3,
  ARCHIVED: 4
}

/** Status group first, then name regardless of case, then id. Returns a new array. */
export const sortMultipliers = (
  items: LoyaltyMultiplierListItem[]
): LoyaltyMultiplierListItem[] =>
  [...items].sort(
    (a, b) =>
      STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
      a.id.localeCompare(b.id)
  )

export const appliesToLabel = ({
  target,
  categoryIds,
  productIds
}: Pick<
  LoyaltyMultiplierListItem,
  'target' | 'categoryIds' | 'productIds'
>): string => {
  if (target === DiscountTarget.CATEGORY) {
    return pluralise(categoryIds.length, 'category', 'categories')
  }
  if (target === DiscountTarget.PRODUCT) {
    return pluralise(productIds.length, 'product', 'products')
  }
  return 'All products'
}

/** "13:30:00" -> "1:30pm" split into the clock part and its suffix */
const clockParts = (time: string): { clock: string; suffix: string } => {
  const [hours, minutes] = time.split(':').map(Number)

  return {
    clock: `${hours % 12 || 12}${
      minutes ? `:${String(minutes).padStart(2, '0')}` : ''
    }`,
    suffix: hours < 12 ? 'am' : 'pm'
  }
}

/** `HH:mm[:ss]` pair -> "1–4pm" or "11am–2pm"; am/pm is written once when both ends share it */
export const formatTimeRange = (start: string, end: string): string => {
  const from = clockParts(start)
  const to = clockParts(end)

  return from.suffix === to.suffix
    ? `${from.clock}–${to.clock}${to.suffix}`
    : `${from.clock}${from.suffix}–${to.clock}${to.suffix}`
}

/** The API sends `HH:mm:ss`; the shared window helpers work in `HH:mm` */
const toWindow = ({ startTime, endTime }: LoyaltyMultiplierDay): DayWindow => ({
  start_time: startTime.slice(0, 5),
  end_time: endTime.slice(0, 5)
})

const sortByWeekday = (days: LoyaltyMultiplierDay[]): LoyaltyMultiplierDay[] =>
  [...days].sort((a, b) => DAYS.indexOf(a.day) - DAYS.indexOf(b.day))

const windowsMatch = (days: LoyaltyMultiplierDay[]): boolean => {
  const [first, ...rest] = days.map(toWindow)

  return rest.every(
    (window) =>
      window.start_time === first.start_time &&
      window.end_time === first.end_time
  )
}

const dayPart = (days: LoyaltyMultiplierDay[]): string => {
  const indexes = days.map(({ day }) => DAYS.indexOf(day))
  const names = days.map(({ day }) => abbreviateDay(day))

  if (days.length === DAYS.length) return 'Every day'

  const isRun = indexes.every(
    (index, position) => position === 0 || index === indexes[position - 1] + 1
  )
  if (days.length >= 3 && isRun) {
    return `${names[0]}–${names[names.length - 1]}`
  }

  return days.length <= 3 ? names.join(', ') : `${days.length} days`
}

/** "Any time", "Mon–Wed", "Tue, Thu, 1–4pm", "4 days, various times" */
export const whenLabel = (days: LoyaltyMultiplierDay[]): string => {
  if (days.length === 0) return 'Any time'

  const sorted = sortByWeekday(days)
  const dayText = dayPart(sorted)

  if (!windowsMatch(sorted)) return `${dayText}, various times`

  const window = toWindow(sorted[0])
  return isAllDayWindow(window)
    ? dayText
    : `${dayText}, ${formatTimeRange(window.start_time, window.end_time)}`
}

/** Each day's window, only when the windows differ — the detail behind "various times" */
export const whenTooltip = (
  days: LoyaltyMultiplierDay[]
): string | undefined => {
  if (days.length === 0 || windowsMatch(days)) return undefined

  return sortByWeekday(days)
    .map((day) => {
      const window = toWindow(day)
      const time = isAllDayWindow(window)
        ? 'all day'
        : formatTimeRange(window.start_time, window.end_time)

      return `${abbreviateDay(day.day)} ${time}`
    })
    .join(' · ')
}

export const boostLabel = (factor: number): string => `${factor}×`
